/**
 * Deterministic spoken-date resolver for the LR Direct voice builder.
 *
 * The AI returns RAW date phrases ("Thursday", "tomorrow", "August 28"); this
 * pure function is the ONLY thing that converts them into real YYYY-MM-DD
 * dates, always against the caller's actual local "today". It follows the
 * project dateOnly.ts policy: all math on local-noon Dates, YMD via local
 * getters — no `new Date("YYYY-MM-DD")`, no UTC.
 *
 * Anything ambiguous or unresolvable is FLAGGED (needsConfirmation) — never
 * silently guessed.
 */

export interface ResolvedDatePhrase {
  /** YYYY-MM-DD when resolved, else null. */
  date: string | null;
  /** True when a plausible-but-uncertain interpretation was used ("next Friday"). */
  ambiguous: boolean;
  /** True when the user must confirm/pick before this can be used. */
  needsConfirmation: boolean;
  /** Human-readable explanation of what was resolved (or why not). */
  note: string | null;
  /**
   * Clock time heard in the phrase ("08:00"), 24h, when one was spoken.
   * INFORMATIONAL ONLY: no stop time is persisted today (wizard stops carry
   * `scheduled_date` only), so this is stripped before date resolution.
   */
  time?: string | null;
}

const MONTHS: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8,
  sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

const WEEKDAYS: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
  sun: 0, mon: 1, tue: 2, tues: 2, wed: 3, thu: 4, thur: 4, thurs: 4, fri: 5, sat: 6,
};

const NUMBER_WORDS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15,
};

const ORDINAL_WORDS: Record<string, number> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7,
  eighth: 8, ninth: 9, tenth: 10, eleventh: 11, twelfth: 12, thirteenth: 13,
  fourteenth: 14, fifteenth: 15, sixteenth: 16, seventeenth: 17, eighteenth: 18,
  nineteenth: 19, twentieth: 20, thirtieth: 30,
  "twenty first": 21, "twenty second": 22, "twenty third": 23, "twenty fourth": 24,
  "twenty fifth": 25, "twenty sixth": 26, "twenty seventh": 27, "twenty eighth": 28,
  "twenty ninth": 29, "thirty first": 31,
};

/**
 * Tokens the AI emits when NO date was spoken (JSON null stringified, filler).
 * These are NOT ambiguous phrases — they must never produce a confirmation.
 */
const JUNK_DATE_TOKENS = new Set([
  "null", "none", "n/a", "na", "no date", "not specified", "not mentioned",
  "unknown", "unsure", "tbd", "no", "nothing", "unspecified",
]);

/**
 * True only when text is a REAL spoken date phrase. Junk tokens ("null",
 * "none", "n/a") and empty values count as NOT spoken — callers should treat
 * them exactly like a missing date (silent fallback, no confirmation flag).
 */
export const isSpokenDatePhrase = (text: string | null | undefined): boolean => {
  if (!text) return false;
  const p = normalize(text);
  return p !== "" && !JUNK_DATE_TOKENS.has(p);
};

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Local-noon Date for a Y/M/D — never `new Date("YYYY-MM-DD")` (UTC shift). */
const atNoon = (y: number, m: number, d: number): Date => new Date(y, m - 1, d, 12, 0, 0, 0);

const addDays = (d: Date, n: number): Date =>
  atNoon(d.getFullYear(), d.getMonth() + 1, d.getDate() + n);

/** YYYY-MM-DD via LOCAL getters (project dateOnly policy). */
export const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmt = (d: Date): string =>
  `${WEEKDAY_NAMES[d.getDay()]} ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

const MONTH_RE = Object.keys(MONTHS).sort((a, b) => b.length - a.length).join("|");
const WEEKDAY_RE = Object.keys(WEEKDAYS).sort((a, b) => b.length - a.length).join("|");

/** Lowercase, hyphenated ordinals split ("twenty-eighth"), punctuation stripped. */
const normalize = (phrase: string): string =>
  phrase
    .toLowerCase()
    .replace(/(\w)-(\w)/g, "$1 $2")
    .replace(/[.,!?;:"']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Strip time-of-day decorations — they don't change the calendar date. */
const stripTimeOfDay = (p: string): string =>
  p
    .replace(/\b(in the )?(morning|afternoon|evening|night|nighttime)\b/g, " ")
    .replace(/\b(a m|p m|am|pm|noon|midday|midnight|eod|end of day|end of the day|close of business|cob)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const stripLeadingFiller = (p: string): string =>
  p
    .replace(/^(pick ?up|drop ?off|deliver|delivery|load|unload)\s+/, "")
    .replace(/^(on|for|by|have it|make it|be)\s+/, "")
    .trim();

const MONTH_NUM_WORDS = Object.keys(NUMBER_WORDS).join("|");

/**
 * Resolve ONE already-normalised, time-stripped date segment. Never called
 * directly — the public `resolveDatePhrase` splits compound phrases and calls
 * this per segment.
 */
function resolveSegment(p: string, base: Date, phrase: string): ResolvedDatePhrase {
  const resolved = (d: Date, note: string | null = null, ambiguous = false): ResolvedDatePhrase => ({
    date: toYMD(d),
    ambiguous,
    needsConfirmation: ambiguous,
    note,
  });
  const flag = (note: string): ResolvedDatePhrase => ({
    date: null,
    ambiguous: false,
    needsConfirmation: true,
    note,
  });

  if (!p) return flag(`Heard "${phrase}" but no date in it — please confirm.`);


  // --- Fixed relative anchors -------------------------------------------------
  if (p === "today" || p === "tonight" || p === "right now") return resolved(base);
  if (p === "tomorrow" || p === "tmrw" || p === "tmr" || p === "tom") return resolved(addDays(base, 1));
  if (p === "day after tomorrow" || p === "the day after tomorrow") return resolved(addDays(base, 2));
  if (p === "yesterday") return flag(`Heard "yesterday" — that date has passed; please confirm.`);

  if (p === "asap" || p === "as soon as possible" || p === "whenever" || p === "anytime" || p === "any time") {
    return flag(`"${phrase}" isn't a calendar date — confirm the actual day.`);
  }
  if (p === "next week") return flag(`"next week" heard — which day?`);
  if (p === "this week") return flag(`"this week" heard — which day?`);
  if (p === "this weekend" || p === "next weekend" || p === "the weekend" || p === "weekend") {
    return flag(`"${phrase}" heard — confirm Saturday or Sunday.`);
  }
  if (p === "end of month" || p === "end of the month") {
    const last = atNoon(base.getFullYear(), base.getMonth() + 2, 0);
    return {
      date: toYMD(last),
      ambiguous: true,
      needsConfirmation: true,
      note: `"end of the month" taken as ${fmt(last)} — please confirm.`,
    };
  }
  if (p === "a couple days" || p === "a couple of days" || p === "couple days" || p === "couple of days") {
    return {
      date: toYMD(addDays(base, 2)),
      ambiguous: true,
      needsConfirmation: true,
      note: `"a couple of days" taken as ${fmt(addDays(base, 2))} — please confirm.`,
    };
  }

  // --- "in N days" / "N days from now" ----------------------------------------
  let m = new RegExp(`^in (\\d+|${MONTH_NUM_WORDS}) days?$`).exec(p)
    ?? new RegExp(`^(\\d+|${MONTH_NUM_WORDS}) days? from now$`).exec(p);
  if (m) {
    const n = /^\d+$/.test(m[1]) ? Number(m[1]) : NUMBER_WORDS[m[1]];
    if (Number.isFinite(n) && n >= 0 && n <= 60) return resolved(addDays(base, n));
    return flag(`Couldn't resolve "${phrase}" — please confirm.`);
  }

  // --- Weekday names ------------------------------------------------------------
  const weekdayRe = `^(${WEEKDAY_RE})$`;
  m = new RegExp(weekdayRe).exec(p)
    ?? new RegExp(`^this (?:coming )?(${WEEKDAY_RE})$`).exec(p);
  if (m) {
    const target = WEEKDAYS[m[1]];
    const diff = (target - base.getDay() + 7) % 7; // next upcoming occurrence (same day = today)
    return resolved(addDays(base, diff));
  }

  // "next Friday" / "Friday next week" → the WEEK-AFTER occurrence, flagged.
  m = new RegExp(`^next (${WEEKDAY_RE})$`).exec(p)
    ?? new RegExp(`^(${WEEKDAY_RE}) next week$`).exec(p);
  if (m) {
    const target = WEEKDAYS[m[1]];
    let diff = (target - base.getDay() + 7) % 7;
    if (diff === 0) diff = 7;
    const d = addDays(base, diff + 7);
    return {
      date: toYMD(d),
      ambiguous: true,
      needsConfirmation: true,
      note: `"${phrase}" taken as ${fmt(d)} (the ${WEEKDAY_NAMES[target]} after this one) — please confirm.`,
    };
  }

  // --- Explicit calendar dates ---------------------------------------------------
  const ordinalWordRe = Object.keys(ORDINAL_WORDS).sort((a, b) => b.length - a.length).join("|");

  // "August 28", "August 28th 2026", "Aug 28", "August twenty eighth"
  m = new RegExp(`^(${MONTH_RE}) (\\d{1,2})(?:st|nd|rd|th)?(?: (\\d{4}))?$`).exec(p)
    ?? new RegExp(`^(${MONTH_RE}) (${ordinalWordRe})(?: (\\d{4}))?$`).exec(p);
  if (m) {
    const month = MONTHS[m[1]];
    const day = /^\d+$/.test(m[2]) ? Number(m[2]) : ORDINAL_WORDS[m[2]];
    return explicitDate(month, day, m[3], base, phrase, resolved, flag);
  }

  // "28th of August", "the twenty eighth of August"
  m = new RegExp(`^(?:the )?(\\d{1,2})(?:st|nd|rd|th)? of (${MONTH_RE})(?: (\\d{4}))?$`).exec(p)
    ?? new RegExp(`^(?:the )?(${ordinalWordRe}) of (${MONTH_RE})(?: (\\d{4}))?$`).exec(p);
  if (m) {
    const day = /^\d+$/.test(m[1]) ? Number(m[1]) : ORDINAL_WORDS[m[1]];
    const month = MONTHS[m[2]];
    return explicitDate(month, day, m[3], base, phrase, resolved, flag);
  }

  // US numeric: "8/28", "08-28", "8/28/26", "8/28/2026"
  m = /^(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?$/.exec(p);
  if (m) {
    const month = Number(m[1]);
    const day = Number(m[2]);
    let year: string | undefined = m[3];
    if (year && year.length === 2) year = `20${year}`;
    return explicitDate(month, day, year, base, phrase, resolved, flag);
  }

  // "Friday the 28th" / "the 28th" / "twenty eighth" → this month, roll forward if past.
  m = new RegExp(`^(?:(?:${WEEKDAY_RE}) )?the (\\d{1,2})(?:st|nd|rd|th)?$`).exec(p)
    ?? new RegExp(`^(?:(?:${WEEKDAY_RE}) )?the (${ordinalWordRe})$`).exec(p)
    ?? new RegExp(`^(\\d{1,2})(?:st|nd|rd|th)?$`).exec(p)
    ?? new RegExp(`^(${ordinalWordRe})$`).exec(p);
  if (m) {
    const day = /^\d+$/.test(m[1]) ? Number(m[1]) : ORDINAL_WORDS[m[1]];
    if (!Number.isFinite(day) || day < 1 || day > 31) return flag(`Couldn't resolve "${phrase}" — please confirm.`);
    let d = atNoon(base.getFullYear(), base.getMonth() + 1, day);
    let note: string | null = null;
    if (d.getDate() !== day) return flag(`"${phrase}" isn't a real date this month — please confirm.`);
    if (d.getTime() < base.getTime()) {
      d = atNoon(base.getFullYear(), base.getMonth() + 2, day);
      if (d.getDate() !== day) return flag(`"${phrase}" isn't a real date — please confirm.`);
      note = `The ${day}${ordinalSuffix(day)} already passed this month, so it was read as ${fmt(d)}.`;
    }
    return resolved(d, note, note !== null);
  }

  return flag(`Couldn't resolve "${phrase}" to a date — please confirm.`);
}

const ordinalSuffix = (d: number): string =>
  d === 1 || d === 21 || d === 31 ? "st" : d === 2 || d === 22 ? "nd" : d === 3 || d === 23 ? "rd" : "th";

/** Shared explicit-date handling: validate the day, infer/roll the year. */
function explicitDate(
  month: number,
  day: number,
  yearStr: string | undefined,
  base: Date,
  phrase: string,
  resolved: (d: Date, note?: string | null, ambiguous?: boolean) => ResolvedDatePhrase,
  flag: (note: string) => ResolvedDatePhrase,
): ResolvedDatePhrase {
  if (!Number.isFinite(month) || month < 1 || month > 12 || !Number.isFinite(day) || day < 1 || day > 31) {
    return flag(`Couldn't resolve "${phrase}" — please confirm.`);
  }
  let year = yearStr ? Number(yearStr) : base.getFullYear();
  let d = atNoon(year, month, day);
  if (d.getMonth() !== month - 1 || d.getDate() !== day) {
    return flag(`"${phrase}" isn't a real calendar date — please confirm.`);
  }
  if (!yearStr && d.getTime() < base.getTime()) {
    year += 1;
    d = atNoon(year, month, day);
    return resolved(d, `"${phrase}" already passed this year, so it was read as ${fmt(d)}.`);
  }
  return resolved(d);
}

// ============================================================================
// Compound phrase handling
// ----------------------------------------------------------------------------
// Real speech rarely gives one clean token. Callers hear things like
// "tomorrow, September 2nd at 8:00 a.m." — a relative anchor + an explicit
// calendar date + a clock time. The AI still returns the RAW phrase only; all
// calendar math below stays deterministic and local-noon based.
// ============================================================================

/** Clock times: "8:00 a m", "8am", "0800 hours", "at 14:30". */
const CLOCK_RE = /\b(?:at|by|around|about|before|after|from|til|till|until)?\s*(\d{1,2})(?::(\d{2}))?\s*(a m|p m|am|pm)\b|\b(?:at|by)\s+(\d{1,2}):(\d{2})\b/;

/**
 * Pull a clock time out of a phrase, normalised to 24h "HH:MM".
 * NOTE: nothing in the wizard/DB stores a stop TIME today (stops carry
 * `scheduled_date` only), so this value is informational — the date resolver
 * strips the time before resolving. Callers may surface it later if a stop
 * time window is ever added.
 */
const extractTime = (lower: string): string | null => {
  const m = CLOCK_RE.exec(lower);
  if (m) {
    const h = Number(m[1] ?? m[4]);
    const min = Number(m[2] ?? m[5] ?? 0);
    const mer = m[3];
    if (Number.isFinite(h) && h <= 24 && min < 60) {
      let hh = h;
      if (mer?.startsWith("p") && h < 12) hh = h + 12;
      if (mer?.startsWith("a") && h === 12) hh = 0;
      return `${String(hh).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    }
  }
  // Bare spoken hour with no meridiem ("Thursday at 10") — informational only.
  const bare = new RegExp(BARE_HOUR_RE.source).exec(lower);
  if (bare) {
    const h = Number(bare[1]);
    if (h >= 1 && h <= 24) return `${String(h).padStart(2, "0")}:00`;
  }
  if (/\bnoon|midday\b/.test(lower)) return "12:00";
  if (/\bmidnight\b/.test(lower)) return "00:00";
  if (/\bmorning\b/.test(lower)) return "08:00";
  if (/\bafternoon\b/.test(lower)) return "14:00";
  if (/\b(evening|night)\b/.test(lower)) return "18:00";
  if (/\b(eod|end of (the )?day|close of business|cob)\b/.test(lower)) return "17:00";
  return null;
};

/**
 * A bare hour introduced by a time preposition ("Thursday at 10", "by 9").
 * Without this, the "10" survived the "at"-split and was read as the 10th of
 * the month, overriding the spoken weekday. Excludes anything that is really a
 * date: "at 8:00", "at 8/28", "at the 10th", "on the 9th".
 */
const BARE_HOUR_RE =
  /\b(?:at|by|around|about|before|after|from|til|till|until)\s+(\d{1,2})(?!\s*(?::|[/-]\d|\d|\s*(?:st|nd|rd|th)\b))\s*(?:o'?\s?clock|oclock|hundred)?\b/g;

/** Remove clock times so only the calendar portion is left. */
const stripClockTime = (p: string): string =>
  p
    .replace(/\b(at|by|around|about|before|after|from|til|till|until)?\s*\d{1,2}(:\d{2})?\s*(a m|p m|am|pm)\b/g, " ")
    .replace(/\b(at|by)\s+\d{1,2}:\d{2}\b/g, " ")
    .replace(/\b\d{3,4}\s*hours\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Strip bare spoken hours ("at 10") before compound splitting. */
const stripBareHour = (p: string): string =>
  p
    .replace(BARE_HOUR_RE, (full, h: string) => {
      const n = Number(h);
      return n >= 1 && n <= 24 ? " " : full;
    })
    .replace(/\s+/g, " ")
    .trim();


/** Filler-only leftovers after time/comma splitting ("the", "on", "sharp"). */
const FILLER_ONLY = /^(on|for|by|at|the|around|about|sharp|please|ok|okay|then|so|and|a|an|it|is|will be|pick ?up|pickup|drop|delivery|deliver)?$/;

type SegKind = "relative" | "weekday" | "explicit" | "other";

const classifySegment = (p: string): SegKind => {
  if (/^(today|tonight|right now|tomorrow|tmrw|tmr|tom|(the )?day after tomorrow|yesterday)$/.test(p)) return "relative";
  if (/^in \d+ days?$|^\d+ days? from now$/.test(p)) return "relative";
  if (new RegExp(`^(next |this (coming )?)?(${WEEKDAY_RE})( next week)?$`).test(p)) return "weekday";
  if (new RegExp(`(${MONTH_RE})`).test(p) || /\d/.test(p) || /\bthe [a-z ]+$/.test(p)) return "explicit";
  return "other";
};

/**
 * Resolve a spoken date phrase against the caller's local "today".
 *
 * Handles COMPOUND phrases: relative anchor + explicit date + weekday + clock
 * time + filler, in any combination. The phrase is split on commas / "and",
 * the clock time is removed, each remaining segment is resolved
 * deterministically, then the segments are reconciled:
 *
 *  - all segments agree                → clean resolve, no confirmation
 *  - weekday label vs explicit date    → explicit date wins (weekday is a label)
 *  - relative vs explicit DISAGREE     → ambiguous, BOTH candidates reported,
 *                                        `date` stays null (never a silent pick)
 *  - nothing resolvable                → confirmation prompt with `date: null`
 *                                        (never a guessed/fallback date)
 *
 * @param phrase raw spoken phrase ("tomorrow, September 2nd at 8:00 a.m.")
 * @param today  the CLIENT's local today (tests pass a fixed local-noon Date)
 */
export function resolveDatePhrase(
  phrase: string | null | undefined,
  today: Date,
): ResolvedDatePhrase {
  if (!isSpokenDatePhrase(phrase)) {
    // Nothing actually spoken (null / "null" / "none" / "n/a") — NOT an
    // ambiguity. Caller falls back to the inherited date silently.
    return { date: null, ambiguous: false, needsConfirmation: false, note: null, time: null };
  }

  const raw = String(phrase);
  const base = atNoon(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const lower = raw.toLowerCase().replace(/(\w)-(\w)/g, "$1 $2");

  // "Thursday or Friday" / "28th or 29th" — multiple options: never pick silently.
  if (/\s+or\s+/.test(lower) && !/\bor\s+so\b/.test(lower)) {
    return {
      date: null, ambiguous: true, needsConfirmation: true, time: extractTime(lower),
      note: `Multiple options heard in "${raw}" — confirm which one.`,
    };
  }

  const time = extractTime(lower);

  const segments: string[] = [];
  for (const chunk of stripBareHour(lower).split(/[,;]|\band\b|\bat\b/)) {
    // Strip the clock BEFORE normalize() removes the colon ("8:00 a.m." must
    // still look like a time when stripClockTime runs).
    const seg = stripLeadingFiller(stripTimeOfDay(normalize(stripClockTime(chunk.replace(/\./g, "")))));
    if (!seg || FILLER_ONLY.test(seg)) continue;
    // "August 28, 2027" — a bare 4-digit year belongs to the previous segment.
    if (/^\d{4}$/.test(seg) && segments.length > 0) segments[segments.length - 1] += ` ${seg}`;
    else segments.push(seg);
  }

  if (segments.length === 0) {
    return {
      date: null, ambiguous: false, needsConfirmation: true, time,
      note: `Heard "${raw}" but no date in it — please confirm.`,
    };
  }

  const parts = segments.map((seg) => ({
    seg,
    kind: classifySegment(seg),
    res: resolveSegment(seg, base, raw),
  }));

  const dated = parts.filter((p) => p.res.date);

  // Nothing resolvable → prompt, and NO fabricated date.
  if (dated.length === 0) {
    const first = parts.find((p) => p.res.note) ?? parts[0];
    return {
      ...first.res,
      date: null,
      needsConfirmation: true,
      time,
      note: first.res.note ?? `Couldn't resolve "${raw}" to a date — please confirm.`,
    };
  }

  if (dated.length === 1) return { ...dated[0].res, time };

  const explicit = dated.filter((p) => p.kind === "explicit");
  const relative = dated.filter((p) => p.kind === "relative");

  // A weekday spoken alongside an explicit date is just a label for that date.
  const anchor = explicit[0] ?? dated[0];
  const anchorDate = anchor.res.date!;

  // Explicit dates that disagree with each other, or a relative anchor that
  // disagrees with the explicit date → ambiguous, show BOTH candidates.
  const conflicting = dated.filter(
    (p) => p.res.date !== anchorDate && (p.kind === "explicit" || p.kind === "relative"),
  );
  if (conflicting.length > 0) {
    const candidates = Array.from(new Set([anchorDate, ...conflicting.map((c) => c.res.date!)]))
      .sort()
      .map((ymd) => {
        const [y, mo, d] = ymd.split("-").map(Number);
        return `${fmt(atNoon(y, mo, d))} (${ymd})`;
      });
    return {
      date: null,
      ambiguous: true,
      needsConfirmation: true,
      time,
      note: `"${raw}" gives two different dates — confirm which: ${candidates.join(" or ")}.`,
    };
  }

  // Everything agrees (or only a weekday label differed) — clean resolve.
  const weekdayMismatch = dated.find((p) => p.kind === "weekday" && p.res.date !== anchorDate);
  const [ay, am, ad] = anchorDate.split("-").map(Number);
  const anchorAsDate = atNoon(ay, am, ad);
  return {
    date: anchorDate,
    ambiguous: anchor.res.ambiguous,
    needsConfirmation: anchor.res.ambiguous,
    time,
    note: weekdayMismatch
      ? `"${raw}" read as ${fmt(anchorAsDate)} — the spoken weekday didn't match, the explicit date was used.`
      : anchor.res.note,
  };
}
